/**
 * policy-packs.ts — R3-Sprint7a-I-fe-B Implementer 落地。
 *
 * 后端：backend_app/app/api/v1/policy_packs.py + indicator_rules.py
 *   GET    /api/v1/policy-packs                          列表
 *   GET    /api/v1/policy-packs/{id}                     详情
 *   POST   /api/v1/policy-packs                          创建      require policy:edit
 *   PATCH  /api/v1/policy-packs/{id}                     更新/状态  require policy:edit
 *   POST   /api/v1/policy-packs/{id}/approve             审批      require policy:approve
 *   DELETE /api/v1/policy-packs/{id}                     删除（仅 draft）
 *   GET    /api/v1/policy-packs/{pack_id}/indicators     指标规则列表
 *   POST   /api/v1/policy-packs/{pack_id}/indicators     新增指标规则
 *   PATCH  /api/v1/policy-packs/{pack_id}/indicators/{indicator_id}  更新
 *   DELETE /api/v1/policy-packs/{pack_id}/indicators/{indicator_id}  删除（仅 draft）
 *
 * threshold_json 6 态 union（schemas/policy_pack.py:ThresholdJSON）：
 *   scalar / dynamic_mentor / composite / qualitative / weighted_sum / bracket_lookup
 *
 * 注：后端实际 PolicyPack.status 枚举为 draft/reviewing/active/archived（不是 deprecated）。
 */
import { apiClient, apiDelete, apiGet, apiPatch, apiPost } from './client';
import type { IndicatorRule, PolicyPack, ThresholdJSON } from '@/types';
import type { Pagination } from './_types/common';
import type { PolicyPackCloneBody } from './_schemas/policy-pack';

/**
 * Sprint21 主菜 1:scope 过滤
 * - 'all'(默认):平台模板 + 本租户副本
 * - 'template':仅平台模板(owner_tenant_id IS NULL)
 * - 'tenant':仅本租户副本(owner_tenant_id = current_tenant)
 *
 * BE 端 list endpoint 改造后会识别 ?scope= 参数;迁移期 FE 同时做客户端兜底过滤,
 * 防 BE 滚动期看到别租户的包(实际 BE 已在 API 层守归属,这里仅是 UX 防御)。
 */
export type PolicyPackScope = 'all' | 'template' | 'tenant';

export interface PolicyPackListParams {
  subject_type?: string;
  category?: string;
  status?: 'draft' | 'reviewing' | 'active' | 'archived' | string;
  region?: string;
  scope?: PolicyPackScope;
  page?: number;
  page_size?: number;
}

export async function listPolicyPacks(
  params: PolicyPackListParams = {},
): Promise<Pagination<PolicyPack>> {
  return apiGet<Pagination<PolicyPack>>('/policy-packs', params as Record<string, unknown>);
}

export async function getPolicyPack(packId: string): Promise<PolicyPack> {
  return apiGet<PolicyPack>(`/policy-packs/${encodeURIComponent(packId)}`);
}

export async function createPolicyPack(body: Partial<PolicyPack>): Promise<PolicyPack> {
  return apiPost<PolicyPack>('/policy-packs', body);
}

export async function updatePolicyPack(
  packId: string,
  body: Partial<PolicyPack>,
): Promise<PolicyPack> {
  return apiPatch<PolicyPack>(`/policy-packs/${encodeURIComponent(packId)}`, body);
}

export async function approvePolicyPack(packId: string): Promise<PolicyPack> {
  return apiPost<PolicyPack>(`/policy-packs/${encodeURIComponent(packId)}/approve`, {});
}

export async function deletePolicyPack(packId: string, opts?: { force?: boolean }): Promise<void> {
  // Sprint 24:force=true 走 archived pack 物理清理路径(BE 限 status='archived' + 0 关联案)
  const qs = opts?.force ? '?force=true' : '';
  await apiDelete(`/policy-packs/${encodeURIComponent(packId)}${qs}`);
}

// ---------------------------------------------------------------------------
// Sprint21 主菜 1:POST /policy-packs/{id}/clone — 平台模板 → 本租户深拷贝
// ---------------------------------------------------------------------------
// 后端:policy_packs.py 新 endpoint(BE Author 切片实现)
//   - 深拷贝 pack + 所有 indicator_rules
//   - 新 pack:owner_tenant_id=current_tenant, cloned_from_pack_id=src.id,
//             code=`${src.code}-${code_suffix}`, status='draft'
//   - RBAC:require_permission('policy:clone')
//
// UX 合同(docs/dev/policy-pack-tenant-isolation-design.md §10):
//   克隆 = 深拷贝、断开关联 — 模板更新不自动 merge 到副本,反之亦然。
//
// 副本默认进入 draft;FE 接到后跳转到编辑页让用户改 indicator_rules。

export async function clonePolicyPack(
  packId: string,
  body: PolicyPackCloneBody,
): Promise<PolicyPack> {
  return apiPost<PolicyPack>(
    `/policy-packs/${encodeURIComponent(packId)}/clone`,
    body,
  );
}

// ---- IndicatorRule (子资源 /policy-packs/{pack_id}/indicators) ----

export interface IndicatorRulePayload {
  code: string;
  name: string;
  calc_key?: string | null;
  threshold_json: ThresholdJSON;
  required?: boolean;
  parent_code?: string | null;
  sort_order?: number;
  policy_ref?: string | null;
  source_text?: string | null;
  condition_group_code?: string | null;
  cumulative_window?: string | null;
  material_type_required?: string | null;
  // Sprint21 主菜 2:分级元数据(BE schemas/policy_pack.py IndicatorRuleBase 已加)
  tier?: 'gate' | 'core' | 'qualitative' | 'bonus';
  group_label?: string | null;
  weight?: number | null;
}

export async function listIndicatorRules(
  packId: string,
): Promise<Pagination<IndicatorRule>> {
  return apiGet<Pagination<IndicatorRule>>(
    `/policy-packs/${encodeURIComponent(packId)}/indicators`,
  );
}

export async function createIndicatorRule(
  packId: string,
  body: IndicatorRulePayload,
): Promise<IndicatorRule> {
  return apiPost<IndicatorRule>(
    `/policy-packs/${encodeURIComponent(packId)}/indicators`,
    body,
  );
}

export async function updateIndicatorRule(
  packId: string,
  indicatorId: string,
  body: Partial<IndicatorRulePayload>,
): Promise<IndicatorRule> {
  return apiPatch<IndicatorRule>(
    `/policy-packs/${encodeURIComponent(packId)}/indicators/${encodeURIComponent(indicatorId)}`,
    body,
  );
}

export async function deleteIndicatorRule(
  packId: string,
  indicatorId: string,
): Promise<void> {
  await apiDelete(
    `/policy-packs/${encodeURIComponent(packId)}/indicators/${encodeURIComponent(indicatorId)}`,
  );
}

// ---------------------------------------------------------------------------
// R3-Sprint13 Phase 2 — AI 起草工作流(上传政策原文 → LLM 解读 → 起草指标集)
// ---------------------------------------------------------------------------

/**
 * POST /policy-packs/upload-doc — 上传政策原文(pdf/docx)。
 *
 * RBAC:PLATFORM + POLICY_EDITOR(rate_limit 5/h)
 *
 * 后端落盘 + 返回 local_path(供 draft-from-doc 引用);policy_doc_url 走对象存储。
 * 客户端校验 size ≤ 20MB 后再发,BE 兜底拒绝。
 */
export interface UploadDocResponse {
  policy_doc_url: string;
  policy_doc_local_path: string;
  size_bytes: number;
  mime: string;
  // BE 实际有返回此字段(upload-doc 用原文件名,upload-doc-from-url 用 URL path 末段)
  original_filename?: string;
}

// Sprint 26:从 URL 抓取政策原文(BE 端 SSRF 守 + 同款落盘 + 同款响应)
export async function uploadPolicyDocFromUrl(url: string): Promise<UploadDocResponse> {
  return apiPost<UploadDocResponse>('/policy-packs/upload-doc-from-url', { url });
}

export async function uploadPolicyDoc(file: File): Promise<UploadDocResponse> {
  const form = new FormData();
  form.append('file', file);
  // FormData 走专门 content-type,interceptor 已识别(client.ts 31-34)
  const res = await apiClient.post<UploadDocResponse>(
    '/policy-packs/upload-doc',
    form,
  );
  return res.data;
}

/**
 * POST /policy-packs/draft-from-doc — LLM 解读上传的政策原文,起草指标集。
 *
 * RBAC:PLATFORM + POLICY_EDITOR(rate_limit 3/h)
 *
 * 同步阻塞 10-30s(LLM 调用) — UI 必须 spinner + 友好提示。
 * 起草成功后返回 status='draft' + ai_drafted=true 的新政策包,人工逐条核对再激活。
 */
export interface DraftFromDocPayload {
  source_doc_local_path: string;
  // B-2 修(BLOCK):BE schema 要求 code/display_name 必填,min_length=1
  code: string;
  display_name: string;
  category: string;
  subject_type: 'enterprise' | 'incubator' | string;
  level: 'national' | 'provincial' | 'municipal' | string;
  region: string;
  version: string;
  effective_year?: number | null;
}

export interface DraftFromDocResponse {
  policy_pack_id: string;
  status: 'draft' | string;
  ai_drafted: true;
  indicator_count: number;
  llm_invocation_id: string;
}

export async function draftFromDoc(
  body: DraftFromDocPayload,
): Promise<DraftFromDocResponse> {
  // R3-Sprint13 hotfix:LLM 调用 50-90s,client.ts 默认 30s timeout 会让 BE 还在跑就被 axios abort。
  // 单独把 timeout 调到 200s(BE LLM_TIMEOUT_SECONDS=180 + 20s buffer)
  const res = await apiClient.post<DraftFromDocResponse>(
    '/policy-packs/draft-from-doc',
    body,
    { timeout: 200_000 },
  );
  return res.data;
}
